import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import PropertyConcierge from "@/components/PropertyConcierge";
import { PRACTICE_AREAS } from "@/data/site";
import { CtaBand } from "@/pages/Home";

const ServicesHero = () => (
  <section className="bg-navy pt-32 pb-20 md:pt-40 md:pb-28" data-testid="services-hero">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Eyebrow light>Our Services</Eyebrow>
      <h1 className="font-serif font-light text-cream text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[1.02]">
        <MaskedLine delay={0.1}>Four areas of law.</MaskedLine>
        <MaskedLine delay={0.25}><em className="italic text-gold">One standard of care.</em></MaskedLine>
      </h1>
      <p className="text-cream/70 max-w-xl mt-8 text-base md:text-lg leading-relaxed">
        Annwil Law Ja focuses on the legal matters that shape family life,
        property and legacy — so every client receives knowledgeable, dedicated
        attention.
      </p>
    </div>
  </section>
);

const ServiceSection = ({ area, index }) => {
  const reversed = index % 2 === 1;
  return (
    <section
      id={area.id}
      data-testid={`service-section-${area.id}`}
      className={`py-20 md:py-28 ${reversed ? "bg-sand" : "bg-cream"} ${index > 0 ? "border-t border-navy/10" : ""}`}
    >
      <div className={`max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
        <Reveal className={reversed ? "lg:order-2" : ""}>
          <div className="relative overflow-hidden border border-navy/10 group">
            <img
              src={area.image}
              alt={area.title}
              className="w-full h-[340px] md:h-[440px] object-cover saturate-[0.55] transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <span className="absolute top-0 left-0 bg-navy text-gold font-serif italic text-xl px-5 py-3">
              {area.number}.
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.15} className={reversed ? "lg:order-1" : ""}>
          <Eyebrow>Practice Area {area.number}</Eyebrow>
          <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-navy">
            {area.title}
          </h2>
          <p className="text-muted-foreground mt-6 text-base md:text-lg leading-relaxed">
            {area.short}
          </p>
          <ul className="mt-8 space-y-3">
            {area.matters.map((matter) => (
              <li key={matter} className="flex items-start gap-3 text-navy text-sm md:text-base">
                <Check size={18} className="text-gold-hover mt-0.5 shrink-0" />
                {matter}
              </li>
            ))}
          </ul>
          <Link
            to="/contact"
            data-testid={`service-${area.id}-cta`}
            className="group inline-flex items-center gap-3 mt-10 text-navy text-sm font-bold uppercase tracking-[0.2em] border-b border-gold pb-1 transition-colors duration-300 hover:text-gold-hover"
          >
            Discuss your {area.title.toLowerCase()} matter
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Services = () => (
  <>
    <ServicesHero />
    <EditorialMarquee dark={false} />
    {PRACTICE_AREAS.map((area, i) => (
      <Fragment key={area.id}>
        <ServiceSection area={area} index={i} />
        {i === 1 && <PropertyConcierge />}
      </Fragment>
    ))}
    <CtaBand />
  </>
);

export default Services;
